import { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight, Check, CircleAlert, Link, LoaderCircle, Plus, Users } from 'lucide-react';
import { COLOR_NAMES } from '../../shared/types';
import { CourierAvatar } from './Brand';
import { parseRoomCode, readPreference, usePreference } from '../ui-utils';

interface RoomEntryProps {
  status: 'offline' | 'connecting' | 'online' | 'reconnecting';
  error: string | null;
  create: (name: string, color: number) => void;
  join: (code: string, name: string, color: number) => void;
  clearError: () => void;
}

export function RoomEntry({ status, error, create, join, clearError }: RoomEntryProps) {
  const incomingRoom = new URLSearchParams(window.location.search).get('room') ?? '';
  const [mode, setMode] = useState<'create' | 'join'>(incomingRoom ? 'join' : 'create');
  const [name, setName] = useState(() => {
    const saved = readPreference('name', '');
    return typeof saved === 'string' ? saved.slice(0, 16) : '';
  });
  const [savedColor, setColor] = usePreference('color', 0);
  const color = Number.isInteger(savedColor) && savedColor >= 0 && savedColor < 8 ? savedColor : 0;
  const [room, setRoom] = useState(incomingRoom.toUpperCase());
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const roomRef = useRef<HTMLInputElement>(null);
  const id = useId();

  useEffect(() => {
    if (error) setPending(false);
  }, [error]);

  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => {
      setPending(false);
      setFormError('Your camp is taking a little longer to answer. Check your connection, then try again.');
    }, 15000);
    return () => window.clearTimeout(timer);
  }, [pending]);

  function changeMode(next: 'create' | 'join') {
    setMode(next);
    setFormError(null);
    clearError();
  }

  const visibleError = formError ?? error;
  const busy = pending || status === 'reconnecting';

  return (
    <section className="entry-panel paper-panel" aria-label="Play Hearthhop" id="play">
      <div className="entry-tabs" role="group" aria-label="Create or join a room">
        <button type="button" className={mode === 'create' ? 'entry-tab active' : 'entry-tab'} aria-pressed={mode === 'create'} disabled={busy} onClick={() => changeMode('create')}>
          <Plus size={17} aria-hidden="true" /> Create a room
        </button>
        <button type="button" className={mode === 'join' ? 'entry-tab active' : 'entry-tab'} aria-pressed={mode === 'join'} disabled={busy} onClick={() => changeMode('join')}>
          <Link size={16} aria-hidden="true" /> Join friends
        </button>
      </div>

      <form className="entry-form" onSubmit={(event) => {
        event.preventDefault();
        if (busy) return;
        const cleanName = name.trim();
        if (!cleanName) {
          setFormError('What should your friends call you? Add a name to get started.');
          nameRef.current?.focus();
          return;
        }
        const code = mode === 'join' ? parseRoomCode(room) : null;
        if (mode === 'join' && !code) {
          setFormError('Use a six-character room code or paste the full invite link.');
          roomRef.current?.focus();
          return;
        }
        clearError();
        setFormError(null);
        try { localStorage.setItem('hearthhop:name', JSON.stringify(cleanName)); } catch { /* Optional preference. */ }
        setPending(true);
        if (mode === 'create') create(cleanName, color);
        else join(code!, cleanName, color);
      }}>
        <div className="form-field">
          <label htmlFor={`${id}-name`}>Your name <span>Make yourself at home.</span></label>
          <input ref={nameRef} id={`${id}-name`} name="nickname" autoComplete="nickname" autoFocus={Boolean(incomingRoom)} placeholder="e.g. Juniper" value={name} maxLength={16} onChange={(event) => setName(event.target.value)} disabled={busy} required aria-describedby={visibleError ? `${id}-error` : undefined} />
        </div>

        {mode === 'join' && (
          <div className="form-field room-field">
            <label htmlFor={`${id}-room`}>Room code or invite link</label>
            <input ref={roomRef} id={`${id}-room`} name="room" className="room-code-input" autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder="ABCDEF or paste an invite link" value={room} onChange={(event) => setRoom(event.target.value)} disabled={busy} required aria-describedby={visibleError ? `${id}-error` : `${id}-room-note`} />
            <span className="sr-only" id={`${id}-room-note`}>Enter the six-character code your friend shared, or their full invite URL.</span>
          </div>
        )}

        <fieldset className="character-picker" disabled={busy}>
          <legend>Pick your little lantern <span>{COLOR_NAMES[color]} · {color + 1}</span></legend>
          <div className="character-options">
            {COLOR_NAMES.map((label, index) => (
              <label className={`character-option${color === index ? ' selected' : ''}`} key={label} title={`${label}, lantern ${index + 1}`}>
                <input type="radio" name={`${id}-character`} value={index} checked={color === index} onChange={() => setColor(index)} aria-label={`${label}, lantern ${index + 1}`} />
                <CourierAvatar color={index} number={index + 1} />
                {color === index && <Check className="character-check" size={11} strokeWidth={3} aria-hidden="true" />}
              </label>
            ))}
          </div>
        </fieldset>

        {visibleError && <div className="inline-error" role="alert" id={`${id}-error`}><CircleAlert size={17} aria-hidden="true" /><span>{visibleError}</span></div>}

        <button type="submit" className="button button-primary entry-submit" disabled={busy}>
          {busy ? <><LoaderCircle className="spin" size={18} aria-hidden="true" /> Finding your camp…</> : <>{mode === 'create' ? 'Start a little adventure' : 'Join the campfire'} <ArrowRight size={19} aria-hidden="true" /></>}
        </button>
        <p className="entry-footnote"><Users size={13} aria-hidden="true" />{mode === 'create' ? 'Make a private room. Bring a friend (or seven).' : 'One name, one lantern. Your friends are waiting.'}</p>
      </form>
    </section>
  );
}
